import { useEffect, useState } from 'react';
import { Link, NavLink, Route, Routes, useLocation } from 'react-router-dom';
import { Brand } from './components/Brand';
import { Icon } from './components/Icon';
import { SupportChat } from './components/SupportChat';
import { WhatsAppFab } from './components/WhatsAppFab';
import { copy, type Language } from './content';
import { useTiltSurfaces } from './motion/useTiltSurfaces';
import {
  ContactPage,
  FaqPage,
  FeaturesPage,
  HomePage,
  LegalPage,
  NotFoundPage,
  PricingPage,
  SecurityPage,
  WorkflowPage,
} from './SitePages';
import { salesEmail, supportEmail } from './services/salesLead';

const pageTitles: Record<string, { ar: string; en: string }> = {
  '/': { ar: 'الرئيسية', en: 'Home' },
  '/features': { ar: 'المميزات', en: 'Features' },
  '/workflow': { ar: 'كيف يعمل', en: 'How it works' },
  '/security': { ar: 'الأمان', en: 'Security' },
  '/pricing': { ar: 'الخطط والأسعار', en: 'Plans and pricing' },
  '/faq': { ar: 'الأسئلة الشائعة', en: 'FAQ' },
  '/contact': { ar: 'طلب عرض سعر', en: 'Request a quote' },
  '/privacy': { ar: 'سياسة الخصوصية', en: 'Privacy policy' },
  '/terms': { ar: 'شروط الاستخدام', en: 'Terms of use' },
  '/refund': { ar: 'سياسة الاسترجاع', en: 'Refund policy' },
};

function RouteEffects({ language }: { language: Language }) {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
    const label = pageTitles[location.pathname]?.[language] || (language === 'ar' ? 'الصفحة' : 'Page');
    document.title = `${label} | Dental Lab System`;
    const description = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (description) {
      description.content = language === 'ar'
        ? `${label} — Dental Lab System لإدارة مختبرات الأسنان.`
        : `${label} — Dental Lab System for connected dental laboratory management.`;
    }
  }, [language, location.pathname]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => entry.isIntersecting && entry.target.classList.add('is-visible')),
      { threshold: 0.1 },
    );
    const elements = document.querySelectorAll('.reveal');
    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, [language, location.pathname]);

  return null;
}

function SiteHeader({ language, onLanguageChange }: { language: Language; onLanguageChange: () => void }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const text = copy[language];

  useEffect(() => setMenuOpen(false), [location.pathname]);

  return (
    <header className="site-header" id="top">
      <div className="container site-header__inner">
        <Brand language={language} tagline={text.brandTagline} />
        <nav className={`main-nav ${menuOpen ? 'is-open' : ''}`} aria-label={language === 'ar' ? 'التنقل الرئيسي' : 'Main navigation'}>
          <NavLink to="/" end>{language === 'ar' ? 'الرئيسية' : 'Home'}</NavLink>
          {text.nav.map((item) => <NavLink to={item.href} key={item.href}>{item.label}</NavLink>)}
          <Link className="button button--primary main-nav__mobile-cta" to="/contact">{text.headerCta}</Link>
        </nav>
        <div className="site-header__actions">
          <button className="language-switch" type="button" onClick={onLanguageChange} aria-label={language === 'ar' ? 'Switch to English' : 'التبديل إلى العربية'}>
            <Icon name="globe" /><span>{language === 'ar' ? 'EN' : 'عربي'}</span>
          </button>
          <Link className="button button--primary site-header__cta" to="/contact">{text.headerCta}<Icon name="arrow" /></Link>
          <button className="menu-button" type="button" aria-expanded={menuOpen} aria-label={menuOpen ? (language === 'ar' ? 'إغلاق القائمة' : 'Close menu') : (language === 'ar' ? 'فتح القائمة' : 'Open menu')} onClick={() => setMenuOpen((value) => !value)}>
            <Icon name={menuOpen ? 'close' : 'menu'} />
          </button>
        </div>
      </div>
    </header>
  );
}

function SiteFooter({ language }: { language: Language }) {
  const text = copy[language];
  return (
    <footer className="site-footer">
      <div className="container site-footer__grid">
        <div className="site-footer__brand"><Brand language={language} tagline={text.brandTagline} /><p>{text.footer.description}</p><span className="system-status"><i />{text.footer.status}</span></div>
        <div className="footer-column"><strong>{text.footer.product}</strong><Link to="/features">{text.nav[0].label}</Link><Link to="/workflow">{text.nav[1].label}</Link><Link to="/pricing">{text.nav[3].label}</Link><Link to="/faq">{text.nav[4].label}</Link></div>
        <div className="footer-column"><strong>{text.footer.company}</strong><Link to="/contact">{text.headerCta}</Link><a href={`mailto:${salesEmail}`}>{salesEmail}</a><a href={`mailto:${supportEmail}`}>{supportEmail}</a></div>
        <div className="footer-column"><strong>{text.footer.legal}</strong><Link to="/privacy">{language === 'ar' ? 'سياسة الخصوصية' : 'Privacy policy'}</Link><Link to="/terms">{language === 'ar' ? 'شروط الاستخدام' : 'Terms of use'}</Link><Link to="/refund">{language === 'ar' ? 'سياسة الاسترجاع' : 'Refund policy'}</Link></div>
      </div>
      <div className="container site-footer__bottom"><p>© {new Date().getFullYear()} Dental Lab System. {text.footer.copyright}</p><span>dentallabsystem.work</span></div>
    </footer>
  );
}

export function App() {
  const [language, setLanguage] = useState<Language>(() => window.localStorage.getItem('dls-language') === 'en' ? 'en' : 'ar');
  const direction = language === 'ar' ? 'rtl' : 'ltr';
  useTiltSurfaces();

  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = direction;
    window.localStorage.setItem('dls-language', language);
  }, [direction, language]);

  return (
    <div className="site-shell site-shell--multipage" dir={direction}>
      <a className="skip-link" href="#main-content">{language === 'ar' ? 'تخطَّ إلى المحتوى' : 'Skip to content'}</a>
      <div className="ambient ambient--one" aria-hidden="true" /><div className="ambient ambient--two" aria-hidden="true" />
      <RouteEffects language={language} />
      <SiteHeader language={language} onLanguageChange={() => setLanguage((current) => current === 'ar' ? 'en' : 'ar')} />
      <main id="main-content">
        <Routes>
          <Route path="/" element={<HomePage language={language} />} />
          <Route path="/features" element={<FeaturesPage language={language} />} />
          <Route path="/workflow" element={<WorkflowPage language={language} />} />
          <Route path="/security" element={<SecurityPage language={language} />} />
          <Route path="/pricing" element={<PricingPage language={language} />} />
          <Route path="/faq" element={<FaqPage language={language} />} />
          <Route path="/contact" element={<ContactPage language={language} />} />
          <Route path="/privacy" element={<LegalPage language={language} kind="privacy" />} />
          <Route path="/terms" element={<LegalPage language={language} kind="terms" />} />
          <Route path="/refund" element={<LegalPage language={language} kind="refund" />} />
          <Route path="*" element={<NotFoundPage language={language} />} />
        </Routes>
      </main>
      <SiteFooter language={language} />
      <WhatsAppFab language={language} />
      <SupportChat language={language} />
    </div>
  );
}
